/*
 * Copyright (c) 2018 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained here in is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */
import { push } from "connected-react-router";
import * as moment from "moment";
import { call, fork, put, select, takeEvery, take } from "redux-saga/effects";

import { verboseCall } from ".";
import { User } from "../../models/User.model";
import { UserData } from "../../models/UserData.model";
import { services } from "../../services";
import { Me, RootState } from "../../types/RootState";
import { getErrorFromCode } from "../../utils/errorHelper";
import {
    ME_GET_ADVISORS_STATS_ACTION,
    ME_STARTUP_NOTIFICATIONS_ACTION,
    ME_UPDATE_USER_ACTION,
    ME_UPDATE_USER_GO_HOME_ACTION,
    setAdvisorsStats,
    setLoading,
    setNotification,
    setSynchronizedUserData,
    setUser,
    NOTIFICATION_SET_ACTION,
} from "../actions";
import { getIsOnline } from "./auth.saga";
import { NotificationItem } from "../../types/NotificationItem";

export const getUserData = (state: RootState) => state.me.userData;
export const getMe = (state: RootState) => state.me;
export const getMyAdvisors = (state: RootState) => state.me.user.advisors;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function* updateUserSaga(action) {
    const user = yield verboseCall(() => services.api.user.updateUser(action.payload));
    yield put(setUser(user));
}

function* updateUserAndGoHomeSaga(action) {
    const user = yield verboseCall(() => services.api.user.updateUser(action.payload));
    yield put(setUser(user));
    yield put(push("/"));
}

function* syncSaga(action) {
    while (true) {
        const me: Me = yield select(getMe);
        if (me.userDataModificationsToSync.length > 0) {
            try {
                const nowTimestamp = moment().toISOString();
                const newUserData: UserData = yield call(() =>
                    services.api.userData.updateUserData(me.userData, me.userDataModificationsToSync)
                );
                yield put(setSynchronizedUserData({ userData: newUserData, beforeTimestamp: nowTimestamp }));
            } catch (e) {
                // Nothing to catch
            }
        }
        yield delay(10000); // TODO Configurable
    }
}

function* getAdvisorsStatsSaga(action) {
    try {
        const isOnline = yield select(getIsOnline);
        if (isOnline) {
            yield put(setLoading(true));
            const advisors: User[] = yield select(getMyAdvisors);
            const advisorsStats = yield verboseCall(() => services.api.userData.statsForAdvisors(advisors));
            yield put(setAdvisorsStats(advisorsStats));
            yield put(setLoading(false));
        } else {
            yield put(setNotification(getErrorFromCode("me.offlineAdvisors")));
        }
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

function* startupNotificationsForMeSaga() {
    const me: Me = yield select(getMe);
    if (!me.user) return;

    yield put(
        setNotification({
            code: "der-login-warning",
            message: "Attention : DER à télécharger et à enregistrer avant utilisation.",
            variant: "warning",
        })
    );
    yield take((action) => action.type === NOTIFICATION_SET_ACTION && action.payload === null);
    if (me.user.team === "MDP") {
        yield delay(1000);
        yield put(
            setNotification({
                code: "mdp-login-warning",
                message: "Attention : pour le réseau MDP - outil à utiliser uniquement lors des Permanences",
                variant: "warning",
                closesAfter: 10000,
            })
        );
    }
}
function* closeNotification() {
    yield delay(10000);
    yield put(setNotification(null));
}

export default function* meSaga() {
    yield fork(startupNotificationsForMeSaga, []);
    yield takeEvery(ME_STARTUP_NOTIFICATIONS_ACTION, startupNotificationsForMeSaga);
    yield takeEvery(ME_UPDATE_USER_GO_HOME_ACTION, updateUserAndGoHomeSaga);
    yield takeEvery(ME_UPDATE_USER_ACTION, updateUserSaga);
    yield takeEvery(ME_GET_ADVISORS_STATS_ACTION, getAdvisorsStatsSaga);
    yield fork(syncSaga, []);
}



// WEBPACK FOOTER //
// ./src/store/sagas/me.saga.ts